'use client';

import { CommonForm } from '@/components/CommonForm';
import { IFormMetadataProps } from '@/components/Form/types';
import { Modal } from '@/components/Modal/Modal';
import { useEnv } from '@/env/client';
import { useCustomSearchParams } from '@/hooks/useCustomSearchParams';
import { useForm } from '@/hooks/useForm';
import { getDynamicsTopicConsents } from '@/tools/forms';
import { DynamicsLeadSource, DynamicsLeadType, IDynamicsFieldMapping } from '@/types/dynamicsForms';
import { parsePhone } from '@lamesarv-sdk/tools';
import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';
import { GeneralForm } from './FormTypes/General';
import { InfluencerForm } from './FormTypes/Influencer';
import { generalSchema, influencerSchema } from './schemas';
import { IEventRegistrationFormProps } from './types';

const getHashSanitized = (hash?: string) => {
  if (!hash || hash?.trim() === '#') return undefined;
  return hash.replaceAll('#', '').trim().toLowerCase();
};

const removeHash = () => {
  if (typeof window === 'undefined') return;
  if ('replaceState' in window.history) {
    window.history.replaceState('', window.document.title, window.location.pathname + window.location.search);
  }
};

// --

interface IInventoryForm extends IFormMetadataProps {
  leadType: string;
  leadSource: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  location: string;
  contact: string;
  product: string;
  comments: string;
  terms: boolean;
  subscribe?: boolean;
  smsOptIn?: boolean;
  attendance?: string;
  interests?: string;
  company_name?: string;
  website_url?: string;
  followers_number?: number;
  content_type?: string;
}

// --

const FormReset = ({ wasSuccessful }: { wasSuccessful: boolean }) => {
  const { reset } = useFormContext();

  useEffect(() => {
    if (!wasSuccessful) return;

    reset();
    reset({
      phone: '',
    });
  }, [wasSuccessful]);

  return null;
};

// --

const mapTypes = {
  general: GeneralForm,
  influencer: InfluencerForm,
};

const mapSchemas = {
  general: generalSchema,
  influencer: influencerSchema,
};

const FormBase = (props: IEventRegistrationFormProps) => {
  const { env } = useEnv();
  const { isLoading, hasError, submitted, sendForm } = useForm();

  const onSubmit = async (data: IInventoryForm, metadata?: Record<string, unknown>) => {
    const mappings: IDynamicsFieldMapping[] = [
      { DataverseFieldName: 'lmrv_formtype', DataverseFieldValue: props.form_type || 'general' },
      {
        DataverseFieldName: 'lmrv_leadtype',
        DataverseFieldValue: data.leadType || props.custom_form_leadtype || String(DynamicsLeadType.marketing),
      },
      {
        DataverseFieldName: 'leadsourcecode',
        DataverseFieldValue: String(DynamicsLeadSource['website-online-inquiry']),
      },
      { DataverseFieldName: 'firstname', DataverseFieldValue: data.firstName || '' },
      { DataverseFieldName: 'lastname', DataverseFieldValue: data.lastName || '' },
      { DataverseFieldName: 'emailaddress1', DataverseFieldValue: data.email || '' },
      { DataverseFieldName: 'mobilephone', DataverseFieldValue: data.phone ? parsePhone(data.phone) : '' },
      { DataverseFieldName: 'lmrv_attendance', DataverseFieldValue: data.attendance || '' },
      { DataverseFieldName: 'lmrv_interests', DataverseFieldValue: data.interests || '' },
      { DataverseFieldName: 'companyname', DataverseFieldValue: data.company_name || '' },
      { DataverseFieldName: 'websiteurl', DataverseFieldValue: data.website_url || '' },
      {
        DataverseFieldName: 'lmrv_followersnumber',
        DataverseFieldValue: data.followers_number !== undefined ? String(data.followers_number) : '',
      },
      { DataverseFieldName: 'lmrv_contenttype', DataverseFieldValue: data.content_type || '' },
      { DataverseFieldName: 'lmrv_comments', DataverseFieldValue: data.comments || '' },
      { DataverseFieldName: 'lmrv_accepttermsandconditions', DataverseFieldValue: data.terms ? '1' : '0' },
      { DataverseFieldName: 'crbdb_segment', DataverseFieldValue: props.dynamics_segment || '' },
    ];

    if (data.subscribe || data.smsOptIn) {
      const topicChannels = [data.subscribe ? 'Email' : undefined, data.smsOptIn ? 'Text' : undefined]
        .filter(Boolean)
        .join(',');
      const topicConsents = getDynamicsTopicConsents({
        complianceProfileId: env.DYNAMICS_COMPLIANCE_PROFILE_ID || '',
        topicIDs: env.DYNAMICS_TOPIC_IDS || '',
        channels: topicChannels,
      });

      mappings.push(...topicConsents);
    }

    if (env.ENABLE_FORMS_CAPTCHA && env.ENABLE_FORMS_CAPTURE_CAPTCHA_SCORE) {
      mappings.push(
        {
          DataverseFieldName: 'lmrv_captchascore',
          DataverseFieldValue: metadata?.captchaScore ? String(metadata?.captchaScore) : '',
        },
        {
          DataverseFieldName: 'lmrv_captchathreshold',
          DataverseFieldValue: metadata?.captchaThreshold ? String(metadata?.captchaThreshold) : '',
        },
      );
    }

    await sendForm({
      formKey: 'EVENT',
      formName: props.custom_name || 'Generic Event Registration Form',
      mappings,
      customDynamicsFormId: props.custom_dynamics_id,
    });
  };

  const wasSuccessful = !isLoading && !hasError && submitted;
  const wasError = !isLoading && hasError;

  useEffect(() => {
    if (wasSuccessful && props.onSuccess) {
      props.onSuccess();
    }
  }, [wasSuccessful]);

  const FormTyped = mapTypes[props.form_type] ?? mapTypes.general;
  const formSchema = mapSchemas[props.form_type] ?? mapSchemas.general;

  return (
    <CommonForm
      id={props.custom_id || 'eventRegistrationForm'}
      schema={formSchema}
      onSubmit={onSubmit}
      isDisabled={isLoading || wasSuccessful}
      tagGroup={props.data_tag_group || 'form'}
      tagItem={props.data_tag ? `${props.data_tag}--form-event-registration` : 'form-event-registration'}
      className="font-helvetica my-8 md:my-16 px-6 md:px-10 lg:container lg:mx-auto"
    >
      <FormTyped {...props} wasError={wasError} wasSuccessful={wasSuccessful} />
      <FormReset wasSuccessful={wasSuccessful} />
    </CommonForm>
  );
};

export const GenericEventRegistrationForm = (props: IEventRegistrationFormProps) => {
  const { searchParams } = useCustomSearchParams();
  const [modalOpen, setModalOpen] = useState(false);

  const hashParameter = getHashSanitized(props?.modal_hash_parameter);

  const onHashChanged = () => {
    if (typeof window !== 'undefined') {
      const hashLocation = getHashSanitized(window?.location?.hash);

      if (hashParameter) {
        setModalOpen(hashLocation === hashParameter);
      }
    }
  };

  useEffect(() => {
    let modalOpenStatus: boolean = false;

    if (props.modal && props.modal_url_parameter && !!searchParams.get(props.modal_url_parameter)) {
      modalOpenStatus = true;
    }

    if (typeof window !== 'undefined') {
      const hashLocation = getHashSanitized(window?.location?.hash);
      const hashParameter = getHashSanitized(props?.modal_hash_parameter);

      if (!!hashParameter && hashLocation === hashParameter) {
        modalOpenStatus = true;
      }
    }

    setModalOpen(modalOpenStatus);

    // Hash Change Control
    if (typeof window !== 'undefined') {
      window.addEventListener('hashchange', onHashChanged);
      return () => {
        window.removeEventListener('hashchange', onHashChanged);
      };
    }
  }, []);

  const onClose = () => {
    setModalOpen(false);
    if (hashParameter) {
      removeHash();
    }
  };

  const onSuccess = () => {
    setTimeout(() => {
      setModalOpen(false);
      if (hashParameter) {
        removeHash();
      }
    }, 2000);
  };

  if (props.modal) {
    return (
      <Modal isOpen={modalOpen} onClose={onClose} useMaxWidth={false} classesClose="right-2 top-2 md:right-8 md:top-8">
        <FormBase {...props} onSuccess={onSuccess} classes={twMerge('border-none', props.classes)} />
      </Modal>
    );
  }

  return <FormBase {...props} />;
};
